import React, { useState, useEffect } from 'react';
import { statsService, projectsService, eventsService, articlesService } from '../services/api';
import { FaProjectDiagram, FaCalendarAlt, FaRegFileAlt, FaChartLine } from 'react-icons/fa';
import Loading from '../components/Common/Loading';

const Statistics = () => {
  const [stats, setStats] = useState({
    total_projects: 0,
    active_projects: 0,
    total_events: 0,
    upcoming_events: 0,
    total_articles: 0
  });
  const [loading, setLoading] = useState(true);
  const [projectsByCategory, setProjectsByCategory] = useState({});
  const [eventsByMonth, setEventsByMonth] = useState({});
  const [articlesCount, setArticlesCount] = useState(0);

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      setLoading(true);
      
      const [dashboardStats, projects, events, articles] = await Promise.all([
        statsService.getDashboard(),
        projectsService.getAll(),
        eventsService.getAll(),
        articlesService.getAll()
      ]);

      // Extraire les stats
      const statsData = dashboardStats.data?.data || dashboardStats.data || {};
      
      // Extraire les projets
      let projectsData = [];
      if (projects.data?.data && Array.isArray(projects.data.data)) {
        projectsData = projects.data.data;
      } else if (Array.isArray(projects.data)) {
        projectsData = projects.data;
      }
      
      // Extraire les événements
      let eventsData = [];
      if (events.data?.data && Array.isArray(events.data.data)) {
        eventsData = events.data.data;
      } else if (Array.isArray(events.data)) {
        eventsData = events.data;
      }

      // Extraire les articles
      let articlesData = [];
      if (articles.data?.data && Array.isArray(articles.data.data)) {
        articlesData = articles.data.data;
      } else if (Array.isArray(articles.data)) {
        articlesData = articles.data;
      }

      console.log('=== STATISTICS DATA ===');
      console.log('Stats:', statsData);
      console.log('Projects count:', projectsData.length);
      console.log('Events count:', eventsData.length);
      console.log('Articles count:', articlesData.length);

      // S'assurer que total_articles existe
      if (!statsData.total_articles) {
        statsData.total_articles = articlesData.length;
      }

      setStats(statsData);
      setArticlesCount(articlesData.length);

      // Stats par catégorie de projets
      const categoryStats = {};
      projectsData.forEach(p => {
        if (p.category) {
          const categoryName = 
            p.category === 'mobility' ? 'International Mobility' :
            p.category === 'solidarity' ? 'Solidarity & Development' :
            p.category === 'human_rights' ? 'Human Rights' :
            p.category === 'education' ? 'Education' :
            p.category === 'environment' ? 'Environment' : p.category;
          categoryStats[categoryName] = (categoryStats[categoryName] || 0) + 1;
        }
      });
      setProjectsByCategory(categoryStats);

      // Stats par mois pour les événements
      const monthStats = {};
      eventsData.forEach(e => {
        if (e.event_date) {
          const month = new Date(e.event_date).toLocaleString('en-US', { month: 'long' });
          monthStats[month] = (monthStats[month] || 0) + 1;
        }
      });
      setEventsByMonth(monthStats);

    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">EFIS Statistics</h1>
        <p className="mt-1 text-gray-600">Overview of the foundation's activities</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-md">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-4xl font-bold">{stats?.total_projects || 0}</p>
              <p className="text-sm opacity-90 mt-1">Total Projects</p>
            </div>
            <FaProjectDiagram size={32} className="opacity-75" />
          </div>
          <div className="mt-4 text-sm opacity-85 bg-white bg-opacity-20 px-2 py-1 rounded w-max">
            {stats?.active_projects || 0} active
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-md">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-4xl font-bold">{stats?.total_articles || articlesCount}</p>
              <p className="text-sm opacity-90 mt-1">Blog Articles</p>
            </div>
            <FaRegFileAlt size={32} className="opacity-75" />
          </div>
          <div className="mt-4 text-sm opacity-85 bg-white bg-opacity-20 px-2 py-1 rounded w-max">
            Articles written
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-md">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-4xl font-bold">{stats?.total_events || 0}</p>
              <p className="text-sm opacity-90 mt-1">Events</p>
            </div>
            <FaCalendarAlt size={32} className="opacity-75" />
          </div>
          <div className="mt-4 text-sm opacity-85 bg-white bg-opacity-20 px-2 py-1 rounded w-max">
            {stats?.upcoming_events || 0} upcoming
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Projets par catégorie */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Projects by Category</h2>
          {Object.keys(projectsByCategory).length === 0 ? (
            <p className="text-center text-gray-500 py-8">No projects</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(projectsByCategory).map(([category, count]) => (
                <div key={category}>
                  <div className="flex justify-between text-sm mb-1 text-gray-700">
                    <span className="capitalize">{category}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${(count / (stats?.total_projects || 1)) * 100}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Événements par mois */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Events by Month</h2>
          {Object.keys(eventsByMonth).length === 0 ? (
            <p className="text-center text-gray-500 py-8">No events</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(eventsByMonth).slice(0, 6).map(([month, count]) => (
                <div key={month}>
                  <div className="flex justify-between text-sm mb-1 text-gray-700">
                    <span className="capitalize">{month}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: `${(count / (stats?.total_events || 1)) * 100}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary Section */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <FaChartLine className="text-purple-600" />
          Activity Summary
        </h2>
        <div className="grid grid-cols-3 gap-6 text-center">
          <div>
            <p className="text-3xl font-bold text-blue-600">{stats?.total_projects || 0}</p>
            <p className="text-sm font-medium text-gray-600 mt-1">Projects</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-purple-600">{stats?.total_articles || articlesCount}</p>
            <p className="text-sm font-medium text-gray-600 mt-1">Articles written</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-green-600">{stats?.total_events || 0}</p>
            <p className="text-sm font-medium text-gray-600 mt-1">Events</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;